<template>
  <v-card class="pa-6">
    <v-card-title class="text-h5 text-primary mb-4">
      Resumo da Inscrição
    </v-card-title>

    <v-card-text>
      <div ref="summaryContent" class="summary-pdf-content">
        <!-- ================= DADOS PESSOAIS ================= -->
        <div class="section-header mb-4">Dados Pessoais</div>
        <v-row dense>
          <v-col cols="12" md="6" v-if="candidateDisplayName">
            <strong>Nome do candidato:</strong> {{ candidateDisplayName }}
          </v-col>

          <v-col cols="12" md="6" v-if="candidateSocialName">
            <strong>Nome social:</strong> {{ candidateSocialName }}
          </v-col>

          <v-col cols="12" md="6" v-if="displayPersonalData.primary_email">
            <strong>E-mail principal:</strong> {{ displayPersonalData.primary_email }}
          </v-col>

          <v-col cols="12" md="6" v-if="displayPersonalData.alternative_email">
            <strong>E-mail alternativo:</strong> {{ displayPersonalData.alternative_email }}
          </v-col>

          <v-col cols="12" md="6" v-if="displayPersonalData.country_of_origin">
            <strong>País de origem:</strong> {{ displayPersonalData.country_of_origin }}
          </v-col>

          <v-col cols="12" md="6" v-if="displayPersonalData.date_of_birth">
            <strong>Data de nascimento:</strong> {{ formatDateBR(displayPersonalData.date_of_birth) }}
          </v-col>

          <v-col cols="12" md="6" v-if="displayPersonalData.gender">
            <strong>Gênero:</strong> {{ displayPersonalData.gender }}
          </v-col>

          <v-col cols="12" md="6" v-if="displayPersonalData.marital_status">
            <strong>Estado civil:</strong> {{ displayPersonalData.marital_status }}
          </v-col>

          <v-col cols="12" md="6" v-if="displayPersonalData.rg">
            <strong>RG:</strong> {{ displayPersonalData.rg }}
          </v-col>

          <v-col cols="12" md="6" v-else-if="displayPersonalData.rne">
            <strong>RNE:</strong> {{ displayPersonalData.rne }}
          </v-col>

          <v-col cols="12" md="6" v-if="displayPersonalData.cpf">
            <strong>CPF:</strong> {{ displayPersonalData.cpf }}
          </v-col>

          <v-col cols="12" md="6" v-else-if="displayPersonalData.passport">
            <strong>Passaporte:</strong> {{ displayPersonalData.passport }}
          </v-col>

          <v-col cols="12" md="6" v-if="displayPersonalData.phone">
            <strong>Telefone:</strong> {{ displayPersonalData.phone }}
          </v-col>

          <v-col cols="12" md="6" v-if="displayPersonalData.alternative_phone">
            <strong>Telefone alternativo:</strong>
            {{ displayPersonalData.alternative_phone }}
          </v-col>
        </v-row>

        <v-row justify="end" class="mt-3 no-pdf">
          <v-btn v-if="inscription.status==='started'" color="primary" @click="$emit('edit-personal')">
            Editar dados pessoais
          </v-btn>
        </v-row>

        <!-- ================= DADOS DA INSCRIÇÃO ================= -->
        <div class="section-header mt-6 mb-4">Dados da Inscrição</div>

        <v-row dense>
          <v-col cols="12" md="6" v-if="inscription.process_name">
            <strong>Processo seletivo:</strong> {{ inscription.process_name }}
          </v-col>

          <v-col cols="12" md="6">
            <strong>Número de inscrição:</strong> {{ subscriptionDisplayId || 'Não disponível' }}
          </v-col>

          <v-col cols="12" md="6" v-if="inscription.research_line">
            <strong>Linha de pesquisa:</strong> {{ lineName(inscription.research_line) }}
          </v-col>

          <v-col cols="12" md="6" v-if="type === 'regular' && inscription.research_group">
            <strong>Grupo de pesquisa:</strong> {{ groupName(inscription.research_group) }}
          </v-col>

          <v-col cols="12" md="6" v-if="type === 'profissional' && inscription.thematic_axis">
            <strong>Eixo temático:</strong> {{ groupName(inscription.thematic_axis) }}
          </v-col>

          <v-col cols="12" md="6" v-if="inscription.project_title">
            <strong>Título do projeto:</strong> {{ inscription.project_title }}
          </v-col>

          <v-col cols="12" md="6" v-if="inscription.degree_level">
            <strong>Nível do curso:</strong> {{ inscription.degree_level }}
          </v-col>

          <template v-if="type === 'special'">
            <v-col cols="12" v-if="inscription.requested_disciplines && inscription.requested_disciplines.length">
              <strong>Disciplinas selecionadas:</strong>
              <div>
                <template v-if="Array.isArray(inscription.requested_disciplines)">
                  <div v-for="(d, i) in inscription.requested_disciplines" :key="`disc-${i}`">
                    {{ className(d) }}
                  </div>
                </template>
                <template v-else>
                  {{ inscription.requested_disciplines }}
                </template>
              </div>
            </v-col>

            <v-col cols="12" md="6" v-if="inscription.reason_to_take_discipline">
              <strong>Motivo para fazer a disciplina:</strong> {{ inscription.reason_to_take_discipline }}
            </v-col>

            <v-col
              cols="12"
              md="6"
              v-if="inscription.graduation_course || inscription.graduation_institution || inscription.graduation_year"
            >
              <strong>Graduação:</strong>
              <div>
                <div v-if="inscription.graduation_course">
                  <strong>Curso:</strong> {{ inscription.graduation_course }}
                </div>
                <div v-if="inscription.graduation_institution">
                  <strong>Instituição:</strong> {{ inscription.graduation_institution }}
                </div>
                <div v-if="inscription.graduation_year">
                  <strong>Ano de conclusão:</strong> {{ inscription.graduation_year }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6" v-if="inscription.is_postgraduate_student">
              <strong>Pós-graduação:</strong>
              <div>
                <div v-if="inscription.postgraduate_course">
                  <strong>Curso:</strong> {{ inscription.postgraduate_course }}
                </div>
                <div v-if="inscription.postgraduate_advisor">
                  <strong>Orientador:</strong> {{ inscription.postgraduate_advisor }}
                </div>
                <div v-if="inscription.postgraduate_project_title">
                  <strong>Projeto:</strong> {{ inscription.postgraduate_project_title }}
                </div>
                <div v-if="inscription.postgraduate_semesters_completed">
                  <strong>Semestres cursados:</strong> {{ inscription.postgraduate_semesters_completed }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6" v-if="inscription.is_employed">
              <strong>Emprego:</strong>
              <div>
                <div v-if="inscription.job_type">
                  <strong>Tipo de emprego:</strong> {{ inscription.job_type }}
                </div>
                <div v-if="inscription.workplace">
                  <strong>Instituição:</strong> {{ inscription.workplace }}
                </div>
                <div v-if="inscription.job_position">
                  <strong>Cargo/Função:</strong> {{ inscription.job_position }}
                </div>
                <div v-if="inscription.working_since">
                  <strong>Data de início:</strong> {{ formatDateBR(inscription.working_since) }}
                </div>
              </div>
            </v-col>
          </template>

          <!-- Documentos obrigatórios -->
          <v-col cols="12" v-if="inscription.documents.length">
            <strong>Documentos:</strong>
            <div v-for="doc in inscription.documents" :key="doc.name">
              <template v-if="doc.url">
                <a href="#" class="download-link" @click.prevent="baixarDocumento('documents', doc.url)">
                  {{ doc.name }}
                </a>
              </template>
              <span v-else>
                {{ doc.name }} — Não enviado
              </span>
            </div>
          </v-col>

          <!-- Cotas -->
          <v-col cols="12" md="6" v-if="type !== 'special'">
            <strong>Participação em cotas:</strong>
            {{ inscription.quota_participation }}
          </v-col>

          <v-col
            cols="12"
            v-if="inscription.quota_participation === 'Pretos, Negros e Pardos' && inscription.image_authorization"
          >
            <strong>Autorização para uso da imagem (fotografia): </strong>
            <span v-html="imageAuthorizationText(inscription.image_authorization)"></span>
          </v-col>
<!--
          <v-col cols="12" v-if="inscription.quota_participation !== 'Não' && inscription.quota_documents.length">
            <strong>Documentos de cotas:</strong>
            <div v-for="doc in inscription.quota_documents" :key="doc.name">
              <a href="#" class="download-link" v-if="doc.url" @click.prevent="baixarDocumento('documents', doc.url)">
                {{ doc.name }}
              </a>
              <span v-else>Não enviado</span>
            </div>
          </v-col>
-->
          <!-- Necessidades especiais -->
          <v-col cols="12" md="6" v-if="type !== 'special'">
            <div>
              <strong>Necessidades especiais:</strong>
              {{ inscription.has_special_needs ? 'Sim' : 'Não' }}
            </div>

            <div v-if="inscription.has_special_needs && inscription.special_needs_description" class="mt-1">
              {{ inscription.special_needs_description }}
            </div>
          </v-col>

          <!-- Publicações -->
          <v-col cols="12" v-if="inscription.publications.length">
            <strong>Publicações:</strong>
            <div v-for="pub in inscription.publications" :key="pub.url">
              <a href="#" class="download-link" @click.prevent="baixarDocumento('publications', pub.url)">
                {{ pub.name }}
              </a>
            </div>
          </v-col>

          <v-col cols="12" class="mb-0 pb-0" v-if="inscription.inscription_date">
            <strong>Data de criação:</strong> {{ formatDateTimeBR(inscription.inscription_date) }}
          </v-col>

          <v-col cols="12" class="mb-0 pt-0" v-if="inscription.updated_at">
            <strong>Data de última alteração:</strong> {{ formatDateTimeBR(inscription.updated_at) }}
          </v-col>

          <v-col cols="12" class="mb-0 pt-0 pdf-emission-date">
            <strong>Data de emissão:</strong> <span class="emission-date-value"></span>
          </v-col>
        </v-row>

      </div>

      <v-row justify="end" class="mt-6">
        <v-btn v-if="inscription.status==='started'" color="primary" class="mr-2" @click="$emit('back')">
          Editar inscrição
        </v-btn>
        <v-btn
          color="primary"
          :loading="isGeneratingPdf"
          :disabled="isGeneratingPdf"
          @click="downloadSummaryPdf"
        >
          Baixar comprovante (PDF)
        </v-btn>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "axios";
import keycloak from "../../plugins/keycloak";
import { downloadFile } from "@/utils/fileDownload";
import { getRegistrationStatus } from '@/utils/registration';
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

export default {
  name: "ApplicationSummary",

  props: {
    personalData: { type: Object, required: true },
    inscriptionData: { type: Object, default: () => ({}) },
    processId: Number,
    type: String,
  },

  data() {
    return {
      userId: null,
      appId: null,
      isGeneratingPdf: false,
      displayPersonalData: {},
      classList: [],
      researchLines: [],
      researchGroups: [],
      inscription: {
        status: "",
        process_name: "",
        subscription_number: "",
        research_line: "",
        research_group: "",
        thematic_axis: "",
        project_title: "",
        degree_level: "",
        documents: [],
        quota_documents: [],
        quota_participation: "Não",
        publications: [],
        has_special_needs: false,
        special_needs_description: "",
        image_authorization: "",
        inscription_date: "",
        updated_at: "",

        requested_disciplines: [],
        reason_to_take_discipline: "",
        graduation_course: "",
        graduation_institution: "",
        graduation_year: "",
        is_postgraduate_student: false,
        postgraduate_course: "",
        postgraduate_advisor: "",
        postgraduate_project_title: "",
        postgraduate_semesters_completed: "",
        is_employed: false,
        job_type: "",
        workplace: "",
        job_position: "",
        working_since: "",
      },
    };
  },

  computed: {
    candidateDisplayName() {
      return String(this.displayPersonalData.full_name || "").trim();
    },
    candidateSocialName() {
      return String(this.displayPersonalData.social_name || "").trim();
    },
    subscriptionDisplayId() {
      const current = this.inscription.subscription_number;
      if (current !== null && current !== undefined && String(current).trim() !== "") {
        return current;
      }

      const fromProp = this.inscriptionData?.subscription_number ?? "";
      return fromProp !== null && fromProp !== undefined ? String(fromProp).trim() : "";
    },
  },
  async created() {
    this.inscription.subscription_number =
      this.inscriptionData?.subscription_number ?? "";

    await this.loadUserId();

    this.displayPersonalData = { ...(this.personalData || {}) };

    if (!this.candidateDisplayName) {
      const profile = await this.loadUserProfile();
      if (profile) {
        this.displayPersonalData = { ...this.displayPersonalData, ...profile };
      }
    }

    await this.loadInscriptionData();
    if (this.$store.state.downloadPdf) {
      this.downloadSummaryPdf();
      this.$store.commit('setDownloadPdf', false);
    }
  },

  methods: {
    async loadUserId() {
      const uuid = keycloak?.tokenParsed?.sub;
      if (!uuid) return;

      const res = await axios.get(`${this.$PROFPATH}/profiles`, {
        params: {
          filters: {
            kc_uuid: uuid,
          },
        },
      });

      this.userId = res.data?.data?.[0]?.id || null;
      return res.data?.data?.[0] || null;
    },

    async loadUserProfile() {
      const profile = await this.loadUserId();
      return profile;
    },

    async loadInscriptionData() {
      if (!this.userId) return;

      const endpoint = this.type === "special" ? "special-applications" : "regular-applications";

      const { data: appRes } = await axios.get(`${this.$BACKPATH}/${endpoint}`, {
        params: {
          "filters[user_id]": this.userId,
          "filters[process_id]": this.processId,
        },
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });

      const app = appRes?.data?.[0];
      if (!app) return;

      this.appId = app.id;
      this.inscription.inscription_date = app.created_at || '';
      this.inscription.updated_at = app.updated_at || '';

      const [resClass, resVacancies] = await Promise.all([
        axios.get(`${this.$APIPATH}/processes/${this.processId}/classes`, { skipAuth: true }),
        axios.get(`${this.$APIPATH}/processes/${this.processId}/vacancies`, { skipAuth: true }),
      ]);

      this.classList = resClass.data?.data || [];
      const vacancies = resVacancies.data?.data || [];

      const lineMap = {};
      const groupArr = [];

      vacancies.forEach((v) => {
        const line = v.research_line;
        const group = v.research_group;

        if (line?.id && !lineMap[line.id]) {
          lineMap[line.id] = { id: Number(line.id), name: line.name };
        }

        if (group?.id && line?.id) {
          groupArr.push({
            id: Number(group.id),
            name: group.name,
            research_line_id: Number(line.id),
          });
        }
      });

      this.researchLines = Object.values(lineMap);
      this.researchGroups = groupArr;

      const actives = this.$store.state.activeProcesses;
      const process = actives[this.processId];
      const phases = process.phases;
      this.inscription.status = getRegistrationStatus(phases);
      this.inscription.process_name = process.name;
      this.inscription.documents = [];
      this.inscription.quota_documents = [];

      if (app.documents) {
        app.documents.forEach((doc) => {
          this.inscription.documents.push({
            name: doc.original_name.replace(/\.pdf$/i, ''),
            url: doc.path,
          });
        });
      }

      this.inscription.subscription_number =
        app.subscription_number ??
        this.inscriptionData?.subscription_number ??
        "";

      this.inscription.research_line =
        app.research_line?.name ?? app.research_line ?? "";

      this.inscription.research_group =
        app.research_group?.name ?? app.research_group ?? "";

      this.inscription.thematic_axis =
        app.thematic_axis?.name ?? app.thematic_axis ?? "";

      this.inscription.project_title = app.project_title || "";
      this.inscription.degree_level = app.degree_level || "";
      /*
      this.inscription.quota_participation = app.has_quotas
        ? app.quota_participation || "Sim"
        : "Não";
      */
      switch (parseInt(app.quota_participation)) {
        case 0:
          this.inscription.quota_participation = "Não";
          break;
        case 1:
          this.inscription.quota_participation = "PCD";
          break;
        case 2:
          this.inscription.quota_participation = "Indígena";
          break;
        case 3:
          this.inscription.quota_participation = "Pretos, Negros e Pardos";
          break;
        case 4:
          this.inscription.quota_participation = "Trans";
          break;
        default:
          break;
      }

      this.inscription.has_special_needs = !!app.has_special_needs;
      this.inscription.special_needs_description = app.special_needs_description || "";
      this.inscription.image_authorization = app.image_authorization || "";

      this.inscription.publications =
        app.publications?.map((url, i) => ({
          name: `Publicação ${i + 1}`,
          url,
        })) || [];

      this.inscription.requested_disciplines = app.requested_disciplines || [];
      this.inscription.reason_to_take_discipline = app.reason_to_take_discipline || "";
      this.inscription.graduation_course = app.graduation_course || "";
      this.inscription.graduation_institution = app.graduation_institution || "";
      this.inscription.graduation_year = app.graduation_year || "";
      this.inscription.is_postgraduate_student = !!app.is_postgraduate_student;
      this.inscription.postgraduate_course = app.postgraduate_course || "";
      this.inscription.postgraduate_advisor = app.postgraduate_advisor || "";
      this.inscription.postgraduate_project_title = app.postgraduate_project_title || "";
      this.inscription.postgraduate_semesters_completed = app.postgraduate_semesters_completed || "";
      this.inscription.is_employed = !!app.is_employed;
      this.inscription.job_type = app.job_type || "";
      this.inscription.workplace = app.workplace || "";
      this.inscription.job_position = app.job_position || "";
      this.inscription.working_since = app.working_since || "";
    },

    formatDateTimeBR(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return this.formatDateBR(dateString);
      const d = date.toLocaleDateString("pt-BR");
      const t = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
      return `${d} ${t}`;
    },

    formatDateBR(dateString) {
      if (!dateString) return "";

      if (typeof dateString === "string") {
        const brMatch = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (brMatch) return dateString;

        const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
          const [, year, month, day] = isoMatch;
          return `${day}/${month}/${year}`;
        }
      }

      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;

      return date.toLocaleDateString("pt-BR");
    },

    imageAuthorizationText(value) {
      if (value === "sim") {
        return `Autorizo o uso, para a primeira etapa da averiguação, da autodeclaração das imagens coletadas no dia da prova escrita. Estou ciente que, caso a análise seja inconclusiva, participarei da etapa complementar de averiguação on-line, conforme a 
    <a href="https://www.pg.unicamp.br/norma/21795/1" target="_blank" rel="noopener" style="text-decoration: none; color: #1976d2;">
      Resolução GR nº 74/2020
    </a>.`;
      } else {
        return `Não autorizo o uso das imagens coletadas no dia da prova escrita para avaliação do fenótipo. Estou ciente que participarei da etapa de averiguação on-line, conforme a 
    <a href="https://www.pg.unicamp.br/norma/21795/1" target="_blank" rel="noopener" style="text-decoration: none; color: #1976d2;">
      Resolução GR nº 74/2020
    </a>.`;
      }
    },
/*
    getRegistrationStatus(phases) {
      for (let i=0; i<phases.length; i++) {
        if (phases[i].type === "registration") {
          const finalDate = new Date(phases[i].end);
          const now = new Date();
          if (now > finalDate) {
            return 'closed'
          }
        }
      }
      return 'started';
    },
*/
    baixarDocumento(type, path) {
      let filePath = path;
      downloadFile(this.$BACKPATH, type, this.appId, this.userId, this.processId, filePath);
    },

    async fetchServerDate() {
      try {
        const res = await axios.head(`${this.$BACKPATH}/regular-applications`, {
          headers: { Authorization: `Bearer ${keycloak.token}` },
        });
        const serverDateHeader = res.headers?.date || res.headers?.['Date'];
        if (serverDateHeader) {
          const sd = new Date(serverDateHeader);
          return `${sd.toLocaleDateString('pt-BR')} ${sd.toLocaleTimeString('pt-BR', { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`;
        }
      } catch {
        // fallback silencioso para data local
      }
      const nd = new Date();
      return `${nd.toLocaleDateString('pt-BR')} ${nd.toLocaleTimeString('pt-BR', { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`;
    },

    async downloadSummaryPdf() {
      const content = this.$refs.summaryContent;
      if (!content || this.isGeneratingPdf) return;
      if (!this.inscription.subscription_number) {
        this.$toast.error("Número de inscrição inexistente.");
        return;
      }
      this.isGeneratingPdf = true;
      try {
        const emissionDate = await this.fetchServerDate();
        const exportWrapper = document.createElement("div");
        exportWrapper.className = "summary-pdf-export-wrapper";
        exportWrapper.style.position = "fixed";
        exportWrapper.style.left = "-99999px";
        exportWrapper.style.top = "0";
        exportWrapper.style.width = "960px";
        exportWrapper.style.padding = "24px";
        exportWrapper.style.background = "#ffffff";
        exportWrapper.style.zIndex = "-1";

        const exportHeader = document.createElement("div");
        exportHeader.className = "summary-pdf-header";
        exportHeader.style.marginBottom = "18px";

        exportHeader.innerHTML = `
          <div style="margin: -12px -12px 14px -12px; padding: 10px 14px; background: #005ca9;">
            <div style="display: flex; align-items: center; gap: 14px;">
              <img src="/logo_unicamp.png" alt="Logo UNICAMP" style="height: 46px; width: auto; object-fit: contain;" />
              <img src="/logo_fe.png" alt="Logo FE" style="height: 46px; width: auto; object-fit: contain;" />
            </div>
          </div>
          <div style="font-size: 24px; font-weight: 700; color: #0d47a1;">Comprovante de Inscrição</div>
          ${
            this.inscription.process_name
              ? `<div style="font-size: 16px; margin-top: 6px;">${this.inscription.process_name}</div>`
              : ""
          }
        `;

        const exportContent = content.cloneNode(true);
        exportContent.classList.add("summary-pdf-export-content");
        exportContent.querySelectorAll(".no-pdf").forEach((el) => el.remove());

        const emissionEl = exportContent.querySelector(".pdf-emission-date");
        if (emissionEl) {
          emissionEl.style.display = "block";
          const span = emissionEl.querySelector(".emission-date-value");
          if (span) span.textContent = emissionDate;
        }

        exportWrapper.appendChild(exportHeader);
        exportWrapper.appendChild(exportContent);
        document.body.appendChild(exportWrapper);

        const images = Array.from(exportWrapper.querySelectorAll("img"));
        await Promise.all(
          images.map((img) => {
            if (img.complete) return Promise.resolve();

            return new Promise((resolve) => {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            });
          })
        );

        const canvas = await html2canvas(exportWrapper, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#ffffff",
          width: exportWrapper.scrollWidth,
          height: exportWrapper.scrollHeight,
        });

        document.body.removeChild(exportWrapper);

        const imageData = canvas.toDataURL("image/jpeg", 0.95);
        const pdf = new jsPDF("p", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const printableWidth = pageWidth - margin * 2;
        const imageHeight = (canvas.height * printableWidth) / canvas.width;

        let remainingHeight = imageHeight;
        let positionY = margin;

        pdf.addImage(imageData, "JPEG", margin, positionY, printableWidth, imageHeight);
        remainingHeight -= pageHeight - margin * 2;

        while (remainingHeight > 0) {
          pdf.addPage();
          positionY = margin - (imageHeight - remainingHeight);
          pdf.addImage(imageData, "JPEG", margin, positionY, printableWidth, imageHeight);
          remainingHeight -= pageHeight - margin * 2;
        }

        const subscription = this.subscriptionDisplayId || "sem-numero";
        const fileName = `comprovante-inscricao-${subscription}.pdf`;
        pdf.save(fileName);
      } catch (error) {
        console.error("Erro ao gerar comprovante em PDF:", error);
      } finally {
        const existingWrapper = document.querySelector(".summary-pdf-export-wrapper");
        if (existingWrapper) {
          document.body.removeChild(existingWrapper);
        }

        this.isGeneratingPdf = false;
      }
    },

    className(classId) {
      for (let i = 0; i < this.classList.length; i++) {
        if (this.classList[i].id == classId) {
          return `${this.classList[i].code} - ${this.classList[i].name}`;
        }
      }
      return classId;
    },

    groupName(groupId) {
      for (let i = 0; i < this.researchGroups.length; i++) {
        if (this.researchGroups[i].id == groupId) {
          return `${this.researchGroups[i].name}`;
        }
      }
      return groupId;
    },

    lineName(lineId) {
      for (let i = 0; i < this.researchLines.length; i++) {
        if (this.researchLines[i].id == lineId) {
          return `${this.researchLines[i].name}`;
        }
      }
      return lineId;
    },
  },
};
</script>

<style scoped>
.section-header {
  background-color: #e3f2fd;
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: bold;
  color: #1976d2;
}

.download-link {
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
}

.summary-pdf-content {
  background-color: #ffffff;
}

.pdf-emission-date {
  display: none;
}

.summary-pdf-export-wrapper {
  position: fixed;
  left: -99999px;
  top: 0;
  width: 960px;
  padding: 24px;
  background: #ffffff;
  z-index: -1;
}

.summary-pdf-export-content {
  font-size: 16px;
  line-height: 1.45;
}

.summary-pdf-export-content .section-header {
  font-size: 18px;
}

.summary-pdf-header {
  margin-bottom: 18px;
}

.summary-pdf-brandbar {
  margin: -12px -12px 14px -12px;
  padding: 10px 14px;
  background: #005ca9;
}

.summary-pdf-logos {
  display: flex;
  align-items: center;
  gap: 14px;
}

.summary-pdf-logos img {
  height: 46px;
  width: auto;
  object-fit: contain;
}

.summary-pdf-title {
  font-size: 24px;
  font-weight: 700;
  color: #0d47a1;
}

.summary-section {
  padding-bottom: 12px;
  margin-bottom: 8px;
}
</style>